// ═══════════════════════════════════════════════════════════════════════════
//  PANEL "SIAPKAN LAGU"
//
//  Menjawab satu pertanyaan: kenapa lagu ini belum bisa dipakai penuh oleh
//  tamu, dan apa yang harus dilakukan supaya bisa.
//
//  ── DISUSUN MENURUT AKIBATNYA, BUKAN MENURUT TABELNYA ─────────────────────
//
//  Daftar centang "ada lirik ✓ / ada instrumen ✗" tidak memberi tahu siapa pun
//  apa yang HILANG bagi tamu. Yang dilihat orang di sini adalah kalimat
//  akibatnya — "tombol vokal mati tidak akan bersih di lagu ini" — karena
//  itulah yang menentukan apakah ia perlu mengerjakannya malam ini atau tidak.
// ═══════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  X, Loader2, Check, AlertTriangle, Upload, Wand2, Music4, FileText, Cpu, RotateCcw,
} from 'lucide-react'
import api, { pesanGalat } from '../lib/api'
import { bacaMidi, namaNot, type TrackMidi } from '../lib/midi'

type Kesiapan = {
  id: string; judul: string; artis: string
  referensi_url?: string | null   // 21 Okt: tautan acuan (YouTube/Spotify) dari Jelajah — metadata
  status: string; status_lisensi: string; nada_dasar: string | null
  ada_asli: boolean; ada_instrumen: boolean; ada_vokal: boolean
  lirik_sinkron: boolean
  ada_melodi: boolean; jml_nada: number
  lirik_konten: string | null; lirik_sumber: string | null
  // Terbaru lebih dulu (view kr_song_kesiapan, migrate_lokal.sql).
  job: Job[] | null
}

type Job = {
  jenis: string; status: string; galat: string | null
  pesan?: string | null; penyedia?: string
  kemajuan?: number; tahap?: string | null; percobaan?: number
  diambil_oleh?: string | null; detak_at?: string | null
  created_at?: string; selesai_at?: string | null
}

type StatusMesin = {
  dikonfigurasi: boolean; penyedia_bawaan: string; otomatis: boolean
  worker: { nama: string; terakhir_at: string; aktif: boolean;
            info?: { perangkat?: string; whisper?: string; versi?: string } | null }[]
  antre: number; gagal_24j: number
  berjalan: { song_id: string; judul: string; jenis: string[]; kemajuan: number; tahap: string | null }[]
}

const JALAN = ['baru', 'dikirim', 'diambil']

function lalu(iso?: string | null): string {
  if (!iso) return '—'
  const d = (Date.now() - new Date(iso).getTime()) / 1000
  if (d < 60) return 'baru saja'
  if (d < 3600) return `${Math.floor(d / 60)} menit lalu`
  if (d < 86400) return `${Math.floor(d / 3600)} jam lalu`
  return `${Math.floor(d / 86400)} hari lalu`
}

export default function Siapkan({ songID, tutup }: { songID: string; tutup: () => void }) {
  const [k, setK] = useState<Kesiapan | null>(null)
  const [galat, setGalat] = useState('')
  const [tab, setTab] = useState<'ringkas' | 'lirik' | 'melodi'>('ringkas')

  const muat = useCallback(async () => {
    try { setK((await api.get<Kesiapan>(`/api/lagu/${songID}/kesiapan`)).data); setGalat('') }
    catch (e) { setGalat(pesanGalat(e)) }
  }, [songID])

  useEffect(() => { muat() }, [muat])

  // Selama ada job yang antre/berjalan, kesiapan dibaca ulang tiap 5 detik —
  // kemajuan % dari Mac terlihat tanpa menekan apa pun.
  const adaJalan = Array.isArray(k?.job) && k!.job!.some(j => JALAN.includes(j.status))
  useEffect(() => {
    if (!adaJalan) return
    const t = setInterval(muat, 5000)
    return () => clearInterval(t)
  }, [adaJalan, muat])

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-8 px-4"
         onClick={tutup}>
      <div className="bg-bg-1 border border-border rounded-2xl w-full max-w-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-5 py-4 border-b border-border">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold truncate">{k?.judul ?? 'Siapkan lagu'}</h2>
            <p className="text-[12px] text-text-3 truncate">{k?.artis}</p>
          </div>
          <button onClick={tutup} className="text-text-3 p-1 -m-1"><X size={18} /></button>
        </div>

        <div className="flex gap-1 px-5 pt-3">
          {([['ringkas', 'Ringkasan'], ['lirik', 'Lirik'], ['melodi', 'Melodi acuan']] as const)
            .map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)}
                className={`px-3 py-1.5 rounded-lg text-[12.5px] border ${
                  tab === id ? 'bg-gold/10 text-gold border-gold/30'
                             : 'border-transparent text-text-3'}`}>{label}</button>
            ))}
        </div>

        <div className="p-5">
          {galat && <div className="text-[12.5px] text-red-400 mb-3">{galat}</div>}
          {!k && !galat && <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>}
          {k && tab === 'ringkas' && <Ringkas k={k} muat={muat} />}
          {k && tab === 'lirik' && <TabLirik k={k} muat={muat} />}
          {k && tab === 'melodi' && <TabMelodi k={k} muat={muat} />}
        </div>
      </div>
    </div>
  )
}

// ── RINGKASAN ──────────────────────────────────────────────────────────────

function Ringkas({ k, muat }: { k: Kesiapan; muat: () => void }) {
  const [workflow, setWorkflow] = useState<{ slug: string; nama: string }[]>([])
  const [pilihStem, setPilihStem] = useState('')
  const [pilihLirik, setPilihLirik] = useState('')
  const [pesan, setPesan] = useState('')
  const [sibuk, setSibuk] = useState(false)

  useEffect(() => {
    // Balasannya OBJEK, bukan array: { dikonfigurasi, workflow, pesan }.
    // Lihat backend handlers/proses.go → ListWorkflow.
    //
    // ── BUG YANG INI PERBAIKI, DAN KENAPA IA LOLOS ──────────────────────────
    //
    // Versi sebelumnya: `setWorkflow(r.data ?? [])`. Yang masuk ke state adalah
    // SELURUH objek, bukan daftarnya. `?? []` tidak menolong sama sekali —
    // objek itu bukan null.
    //
    // Akibatnya baru meledak dua lapis kemudian, di <Jalur>:
    //
    //     if (workflow.length === 0) return null   ← undefined === 0 → false
    //     workflow.map(...)                        ← "n.map is not a function"
    //
    // Penjaga `.length === 0` itu tampak seperti penjaga, tapi ia hanya
    // menjaga array KOSONG — bukan nilai yang bukan array. Ia justru
    // MELEWATKAN kasus yang sesungguhnya berbahaya.
    //
    // Tipe generik `api.get<...[]>` ikut menyesatkan: TypeScript percaya saja
    // pada tipe yang kita tulis sendiri, dan tidak pernah memeriksa bahwa
    // server benar-benar mengirim bentuk itu. Yang dijanjikan tipe dan yang
    // dikirim server adalah dua hal berbeda, dan hanya yang kedua yang nyata.
    api.get<{ dikonfigurasi: boolean; workflow?: { slug: string; nama: string }[]; pesan?: string }>(
      '/api/musicai/workflow')
      .then(r => {
        // Diambil ke variabel dulu. `Array.isArray(r.data?.workflow)` benar saat
        // dijalankan, tapi penyempitan tipe TypeScript tidak berpindah dari
        // ekspresi optional-chain ke `r.data.workflow` di cabang berikutnya —
        // dan yang gagal adalah build, bukan aplikasinya.
        const wf = r.data?.workflow
        setWorkflow(Array.isArray(wf) ? wf : [])
      })
      // Daftar workflow kosong BUKAN galat yang perlu menutup panel: kunci
      // Music AI boleh saja belum diisi, dan jalur manual tetap terpakai.
      .catch(() => setWorkflow([]))
  }, [])

  // slug kosong = engine lokal (Mac). Slug terisi = Music AI.
  async function proses(jenis: 'stem' | 'lirik', slug: string) {
    setSibuk(true); setPesan('')
    try {
      if (slug) {
        await api.post(`/api/lagu/${k.id}/proses`, { jenis, workflow: slug, penyedia: 'musicai' })
        setPesan('Job dikirim ke Music AI — hasilnya masuk sendiri kalau sudah selesai.')
      } else {
        const r = await api.post<{ pesan?: string }>(`/api/lagu/${k.id}/proses-lokal`, { jenis: [jenis] })
        setPesan(r.data?.pesan ?? 'Job dijadwalkan untuk PC karaoke.')
      }
      muat()
    } catch (e) { setPesan(pesanGalat(e)) } finally { setSibuk(false) }
  }

  const daftarJob = Array.isArray(k.job) ? k.job : []
  const jobStem = daftarJob.find(j => j.jenis === 'stem')
  const jobLirik = daftarJob.find(j => j.jenis === 'lirik')

  return (
    <div className="space-y-2.5">
      <MesinLokal k={k} muat={muat} />
      {k.referensi_url && (
        <div className="text-[11.5px] text-text-3">
          Acuan: <a href={k.referensi_url} target="_blank" rel="noreferrer" className="text-gold underline underline-offset-2 break-all">{k.referensi_url}</a>
          <span className="ml-1">— untuk mencocokkan judul/versi/durasi; berkasnya tetap lewat Unggah atau Impor dari link file.</span>
        </div>
      )}

      <Baris ok={k.ada_asli}
        judul="Berkas asli"
        akibat={k.ada_asli
          ? 'Lagu bisa diputar.'
          : 'Lagu TIDAK bisa diputar sama sekali. Unggah berkasnya dulu — tanpa ini semua baris di bawah tidak ada gunanya.'}>
        {/* Tombol unggahnya BARU ADA sekarang. Sebelum ini kalimat di atas
            menyuruh "unggah berkasnya dulu" sementara tombolnya tidak pernah
            dibuat di mana pun — dan orang mencarinya sampai menyerah. */}
        <UnggahAsli songID={k.id} sesudah={muat} />
      </Baris>

      <Baris ok={k.lirik_sinkron}
        judul="Lirik bersinkron waktu"
        akibat={k.lirik_sinkron
          ? 'Lirik berjalan di layar TV.'
          : 'Layar TV tidak menampilkan lirik berjalan. Tamu yang belum hafal akan berhenti menyanyi di tengah.'}>
        {!k.lirik_sinkron && (
          <Jalur jenis="lirik" job={jobLirik} workflow={workflow}
            nilai={pilihLirik} set={setPilihLirik} sibuk={sibuk}
            jalan={() => proses('lirik', pilihLirik)} />
        )}
      </Baris>

      <Baris ok={k.ada_instrumen}
        judul="Trek instrumen (vokal terpisah)"
        akibat={k.ada_instrumen
          ? 'Tombol "vokal mati" berpindah trek — bersih.'
          : 'Tombol "vokal mati" memakai pembatalan kanal tengah: bass dan snare ikut menipis, dan tamu akan menyalahkan sound system.'}>
        {!k.ada_instrumen && (
          <Jalur jenis="stem" job={jobStem} workflow={workflow}
            nilai={pilihStem} set={setPilihStem} sibuk={sibuk}
            jalan={() => proses('stem', pilihStem)} />
        )}
      </Baris>

      <Baris ok={k.ada_melodi}
        judul={`Melodi acuan${k.ada_melodi ? ` — ${k.jml_nada} nada` : ''}`}
        akibat={k.ada_melodi
          ? 'Skor menilai KETEPATAN NADA.'
          : 'Skor menilai ketepatan irama saja, dan layar skor mengatakannya. Ketepatan nada tidak dinilai — tidak ada nada acuan untuk dibandingkan.'} />

      {pesan && <div className="text-[12.5px] text-gold pt-1">{pesan}</div>}

      {workflow.length === 0 && (
        <p className="text-[11.5px] text-text-3 pt-2 leading-relaxed border-t border-border mt-3">
          Pemrosesan memakai engine lokal di PC karaoke (pisah vokal Demucs + lirik
          Whisper). Lirik tetap bisa diisi/dikoreksi manual lewat tab Lirik —
          lirik manual tidak akan ditimpa mesin.
        </p>
      )}
    </div>
  )
}

// ── ENGINE LOKAL (Mac) ─────────────────────────────────────────────────────
//
// Status Mac pemilik (terakhir menyapa server) + job lokal lagu ini dengan
// kemajuan % dan tahap dari detak worker. Lihat backend handlers/worker.go.
function MesinLokal({ k, muat }: { k: Kesiapan; muat: () => void }) {
  const [st, setSt] = useState<StatusMesin | null>(null)
  const [pesan, setPesan] = useState('')
  const [sibuk, setSibuk] = useState(false)

  const muatStatus = useCallback(() => {
    api.get<StatusMesin>('/api/worker/status')
      .then(r => setSt(r.data && Array.isArray(r.data.worker) ? r.data : null))
      .catch(() => setSt(null))
  }, [])
  useEffect(() => {
    muatStatus()
    const t = setInterval(muatStatus, 15000)
    return () => clearInterval(t)
  }, [muatStatus])

  // Job lokal terbaru per jenis.
  const lokal: Job[] = []
  for (const j of (Array.isArray(k.job) ? k.job : [])) {
    if (j.penyedia !== 'lokal') continue
    if (!lokal.some(x => x.jenis === j.jenis)) lokal.push(j)
  }
  const gagal = lokal.filter(j => j.status === 'gagal').map(j => j.jenis)
  const jalan = lokal.some(j => JALAN.includes(j.status))

  async function proses(jenis: string[]) {
    setSibuk(true); setPesan('')
    try {
      const r = await api.post<{ pesan?: string }>(`/api/lagu/${k.id}/proses-lokal`, { jenis })
      setPesan(r.data?.pesan ?? 'Job dijadwalkan.')
      muat(); muatStatus()
    } catch (e) { setPesan(pesanGalat(e)) } finally { setSibuk(false) }
  }

  const mac = st?.worker?.[0]
  const NAMA_JENIS: Record<string, string> = { stem: 'Vokal / musik', lirik: 'Lirik otomatis', analisa: 'Nada & BPM' }

  return (
    <div className="rounded-xl border border-border bg-bg-2 px-3.5 py-3">
      <div className="flex items-center gap-2">
        <Cpu size={14} className="text-gold shrink-0" />
        <div className="text-[13px] font-medium flex-1">Engine lokal (PC karaoke)</div>
        {st && (
          <span className={`text-[11px] flex items-center gap-1.5 ${mac?.aktif ? 'text-emerald-400' : 'text-text-3'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${mac?.aktif ? 'bg-emerald-400' : 'bg-text-3'}`} />
            {mac ? `Worker terakhir aktif ${lalu(mac.terakhir_at)}` : 'Worker belum pernah tersambung'}
          </span>
        )}
      </div>

      {st && !st.dikonfigurasi && (
        <div className="mt-1.5 text-[11.5px] text-amber-400 leading-relaxed">
          Token worker belum diisi di server — PC belum bisa mengambil job.
          Jalankan ./205-env-karaoke-worker.command lalu ./206-pasang-worker-karaoke-mac.command.
        </div>
      )}
      {st && (
        <div className="mt-1 text-[11px] text-text-3">
          Antrean: {st.antre} lagu{st.gagal_24j > 0 ? ` · ${st.gagal_24j} job gagal 24 jam terakhir` : ''}
          {mac?.info?.perangkat ? ` · ${mac.nama} (${mac.info.perangkat}, whisper ${mac.info.whisper ?? '?'})` : ''}
          {st.otomatis ? ' · unggahan berkas asli diproses otomatis' : ''}
        </div>
      )}

      {lokal.length > 0 && (
        <div className="mt-2.5 space-y-2">
          {lokal.map(j => (
            <div key={j.jenis}>
              <div className="flex items-center gap-2 text-[12px]">
                <span className="w-28 shrink-0 text-text-2">{NAMA_JENIS[j.jenis] ?? j.jenis}</span>
                {j.status === 'berhasil' && <span className="text-emerald-400 flex items-center gap-1"><Check size={12} /> selesai {lalu(j.selesai_at)}</span>}
                {j.status === 'baru' && <span className="text-text-3">menunggu PC…{j.tahap ? ` (${j.tahap})` : ''}</span>}
                {j.status === 'diambil' && <span className="text-gold truncate">{j.kemajuan ?? 0}% · {j.tahap ?? 'diproses'}</span>}
                {j.status === 'gagal' && <span className="text-red-400 truncate" title={j.pesan ?? ''}>gagal: {j.pesan ?? j.galat ?? '—'}</span>}
              </div>
              {j.status === 'diambil' && (
                <div className="mt-1 ml-28 h-1 rounded bg-bg-1 overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${j.kemajuan ?? 0}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {gagal.length > 0 && (
          <button onClick={() => proses(gagal)} disabled={sibuk || !k.ada_asli}
            className="px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 text-[12px]
                       disabled:opacity-50 inline-flex items-center gap-1.5">
            <RotateCcw size={12} /> Coba lagi yang gagal
          </button>
        )}
        <button onClick={() => proses([])} disabled={sibuk || jalan || !k.ada_asli}
          className="px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold text-[12px]
                     disabled:opacity-50 inline-flex items-center gap-1.5">
          {sibuk ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
          {lokal.length ? 'Proses ulang semua' : 'Proses semua di PC'}
        </button>
        {!k.ada_asli && <span className="text-[11px] text-text-3">Unggah berkas asli dulu.</span>}
      </div>
      {pesan && <div className="mt-1.5 text-[11.5px] text-gold leading-relaxed">{pesan}</div>}
    </div>
  )
}

function Baris({ ok, judul, akibat, children }: {
  ok: boolean; judul: string; akibat: string; children?: ReactNode
}) {
  return (
    <div className={`rounded-xl border px-3.5 py-3 ${
      ok ? 'border-emerald-500/25 bg-emerald-500/[0.05]' : 'border-border bg-bg-2'}`}>
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 shrink-0 ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>
          {ok ? <Check size={15} /> : <AlertTriangle size={15} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium">{judul}</div>
          <div className="text-[11.5px] text-text-3 leading-relaxed mt-0.5">{akibat}</div>
          {children}
        </div>
      </div>
    </div>
  )
}

// ── UNGGAH BERKAS ASLI ─────────────────────────────────────────────────────
//
// Kemajuannya DITAMPILKAN. Berkas 8 MB pada unggahan rumahan bisa satu menit
// lebih, dan tombol yang cuma berubah jadi "menunggu…" tanpa angka tidak bisa
// dibedakan dari macet — kesalahan yang sudah beberapa kali terjadi di skrip
// deploy hari ini, dan tidak perlu diulang di layar.
function UnggahAsli({ songID, sesudah }: { songID: string; sesudah: () => void }) {
  const [maju, setMaju] = useState<number | null>(null)
  const [pesan, setPesan] = useState('')
  const [galat, setGalat] = useState('')

  async function kirim(f: File) {
    setGalat(''); setPesan(''); setMaju(0)
    const fd = new FormData()
    fd.append('berkas', f)
    fd.append('mutu', 'utama')
    try {
      const r = await api.post(`/api/lagu/${songID}/berkas`, fd, {
        // Content-Type SENGAJA tidak diset. Browser yang menuliskannya lengkap
        // dengan boundary multipart; menuliskannya sendiri menghapus boundary
        // itu dan server menolak dengan pesan yang tidak menyebut penyebabnya.
        onUploadProgress: e => {
          if (e.total) setMaju(Math.round((e.loaded / e.total) * 100))
        },
      })
      setPesan(String((r.data as { pesan?: string })?.pesan ?? 'Berkas tersimpan.'))
      sesudah()
    } catch (e) {
      setGalat(pesanGalat(e))
    } finally { setMaju(null) }
  }

  return (
    <div className="mt-2">
      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                        bg-gold/10 border border-gold/30 text-gold text-[12px] cursor-pointer">
        <Upload size={12} />
        {maju === null ? 'Unggah berkas lagu' : `Mengunggah ${maju}%`}
        <input type="file" className="hidden"
          accept=".mp3,.m4a,.wav,.flac,.mp4,.mov,.mkv,.webm,audio/*,video/*"
          disabled={maju !== null}
          onChange={e => { const f = e.target.files?.[0]; if (f) kirim(f); e.target.value = '' }} />
      </label>
      {maju !== null && (
        <div className="mt-1.5 h-1 rounded bg-bg-1 overflow-hidden">
          <div className="h-full bg-gold transition-all" style={{ width: `${maju}%` }} />
        </div>
      )}
      {pesan && <div className="mt-1.5 text-[11.5px] text-gold leading-relaxed">{pesan}</div>}
      {galat && <div className="mt-1.5 text-[11.5px] text-red-400 leading-relaxed">{galat}</div>}
      <ImporURL songID={songID} sesudah={sesudah} />
    </div>
  )
}

// ── IMPOR DARI URL (20 Okt 2026) ───────────────────────────────────────────
//
// Server yang mengunduh (link file langsung / Drive / Dropbox / OneDrive),
// di latar — nginx memutus /api/ setelah 180 detik, jadi kemajuannya
// ditanyakan tiap 2 detik lewat GET /impor-url. Situs streaming ditolak server.
type StatusImpor = {
  url: string; tahap: 'unduh' | 'simpan' | 'selesai' | 'gagal'
  byte: number; total: number; galat?: string; pesan?: string
  di?: 'vm' | 'pc'   // 20 Okt: 'pc' = job unduh dikerjakan PC karaoke (byte = persen)
}

function mb(b: number) { return `${(b / 1048576).toFixed(1)} MB` }

function ImporURL({ songID, sesudah }: { songID: string; sesudah: () => void }) {
  const [buka, setBuka] = useState(false)
  const [url, setUrl] = useState('')
  const [st, setSt] = useState<StatusImpor | null>(null)
  const [galat, setGalat] = useState('')
  const [sibuk, setSibuk] = useState(false)

  const jalan = st?.tahap === 'unduh' || st?.tahap === 'simpan'

  const cek = useCallback(async () => {
    try {
      const r = await api.get<{ ada: boolean; status?: StatusImpor }>(`/api/lagu/${songID}/impor-url`)
      if (r.data.ada && r.data.status) setSt(r.data.status)
    } catch { /* diam — ditanya lagi 2 detik kemudian */ }
  }, [songID])

  // Saat panel dibuka, impor yang mungkin masih berjalan (mis. halaman
  // sempat ditutup) langsung tampil lagi.
  useEffect(() => { cek() }, [cek])

  useEffect(() => {
    if (!jalan) return
    const t = setInterval(cek, 2000)
    return () => clearInterval(t)
  }, [jalan, cek])

  const selesaiRef = useRef('')
  useEffect(() => {
    if (st?.tahap === 'selesai' && selesaiRef.current !== st.url) {
      selesaiRef.current = st.url
      sesudah()
    }
  }, [st, sesudah])

  async function mulai() {
    const u = url.trim()
    if (!u) return
    setGalat(''); setSibuk(true)
    try {
      await api.post(`/api/lagu/${songID}/impor-url`, { url: u })
      setSt({ url: u, tahap: 'unduh', byte: 0, total: 0 })
      setUrl('')
    } catch (e) { setGalat(pesanGalat(e)) } finally { setSibuk(false) }
  }

  return (
    <div className="mt-2">
      {!buka && !jalan && (
        <button type="button" onClick={() => setBuka(true)}
          className="text-[11.5px] text-text-3 underline underline-offset-2 hover:text-gold">
          atau impor dari link file (Drive / Dropbox / server)
        </button>
      )}
      {(buka || jalan) && (
        <div className="rounded-lg border border-line bg-bg-1/60 p-2.5 space-y-1.5">
          <div className="text-[11.5px] text-text-2">
            Impor dari URL — link <b>file</b> mp3/mp4/wav/flac/mkv (Google Drive, Dropbox, OneDrive,
            server sendiri, penyedia berlisensi). Diunduh oleh PC karaoke dan disimpan di sana; kalau PC mati,
            server yang mengunduh. Link halaman YouTube/TikTok/Spotify tidak bisa.
          </div>
          {!jalan && (
            <div className="flex gap-1.5">
              <input value={url} onChange={e => setUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') mulai() }}
                placeholder="https://…/lagu.mp4"
                className="flex-1 min-w-0 px-2 py-1.5 rounded-md bg-bg-0 border border-line text-[12px]" />
              <button type="button" onClick={mulai} disabled={sibuk || !url.trim()}
                className="px-3 py-1.5 rounded-md bg-gold/15 border border-gold/40 text-gold text-[12px]
                           disabled:opacity-40 inline-flex items-center gap-1">
                {sibuk ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Impor
              </button>
            </div>
          )}
          {jalan && st && (
            <div>
              <div className="text-[11.5px] text-gold inline-flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" />
                {st.di === 'pc' ? `PC karaoke: ${st.pesan || 'mengunduh'} (${st.byte}%)`
                  : st.tahap === 'simpan' ? 'Menyimpan…'
                  : st.total > 0 ? `Mengunduh ${mb(st.byte)} / ${mb(st.total)} (${Math.round(st.byte * 100 / st.total)}%)`
                  : `Mengunduh ${mb(st.byte)}…`}
              </div>
              {st.total > 0 && (
                <div className="mt-1 h-1 rounded bg-bg-1 overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${Math.min(100, st.byte * 100 / st.total)}%` }} />
                </div>
              )}
              <div className="mt-1 text-[11px] text-text-3 break-all">{st.url}</div>
            </div>
          )}
          {st?.tahap === 'selesai' && st.pesan && (
            <div className="text-[11.5px] text-gold leading-relaxed inline-flex items-start gap-1">
              <Check size={12} className="mt-0.5 shrink-0" /> {st.pesan}
            </div>
          )}
          {st?.tahap === 'gagal' && st.galat && (
            <div className="text-[11.5px] text-red-400 leading-relaxed">{st.galat}</div>
          )}
          {galat && <div className="text-[11.5px] text-red-400 leading-relaxed">{galat}</div>}
        </div>
      )}
    </div>
  )
}

function Jalur({ jenis, job, workflow, nilai, set, sibuk, jalan }: {
  jenis: string
  job?: Job
  workflow: { slug: string; nama: string }[]
  nilai: string; set: (v: string) => void; sibuk: boolean; jalan: () => void
}) {
  // Job yang masih berjalan menggantikan tombolnya, bukan ditampilkan di
  // sebelahnya. Tombol yang tetap bisa ditekan saat job berjalan akan ditekan
  // — dan jawabannya "sudah punya job yang belum selesai", tiap kali.
  if (job && JALAN.includes(job.status)) {
    return <div className="mt-2 text-[11.5px] text-gold flex items-center gap-1.5">
      <Loader2 size={12} className="animate-spin" /> Job {jenis}{job.penyedia === 'lokal' ? ' (PC)' : ''}{' '}
      {job.status === 'diambil' ? `${job.kemajuan ?? 0}% — ${job.tahap ?? 'diproses'}` : 'menunggu…'}
    </div>
  }
  const gagal = job?.status === 'gagal'
    ? <div className="mt-2 text-[11.5px] text-red-400 leading-relaxed">
        Job {jenis} gagal: {job.pesan ?? job.galat ?? 'tanpa keterangan'}
      </div>
    : null
  // `Array.isArray`, BUKAN `.length === 0`.
  //
  // Penjaga lama hanya menangkap array KOSONG. Nilai yang BUKAN array — objek,
  // null, string — punya `.length` undefined, `undefined === 0` bernilai false,
  // dan penjaganya justru MELEWATKANNYA ke `.map` di bawah. Itu persis yang
  // meruntuhkan layar ini 25 Agu 2026.
  //
  // Ini bukan kehati-hatian berlebihan di satu tempat: setiap `.map` atas data
  // dari server layak diperlakukan begini, karena tipe TypeScript-nya cuma
  // janji yang kita tulis sendiri — server tidak pernah ikut menandatanganinya.
  if (!Array.isArray(workflow) || workflow.length === 0) {
    return (
      <>
        {gagal}
        <button onClick={jalan} disabled={sibuk}
          className="mt-2 px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold
                     text-[12px] disabled:opacity-50 inline-flex items-center gap-1.5">
          <Cpu size={12} /> {job?.status === 'gagal' ? 'Coba lagi di PC' : 'Proses di PC'}
        </button>
      </>
    )
  }
  return (
    <>
    {gagal}
    <div className="mt-2 flex items-center gap-1.5">
      <select value={nilai} onChange={e => set(e.target.value)}
        className="flex-1 bg-bg-1 border border-border rounded-lg px-2 py-1.5 text-[12px]">
        <option value="">Pilih workflow…</option>
        {workflow.map(w => <option key={w.slug} value={w.slug}>{w.nama}</option>)}
      </select>
      <button onClick={jalan} disabled={sibuk}
        className="px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold
                   text-[12px] disabled:opacity-50 flex items-center gap-1.5">
        <Wand2 size={12} /> {nilai ? 'Proses (Music AI)' : 'Proses di PC'}
      </button>
    </div>
    </>
  )
}

// ── LIRIK MANUAL ───────────────────────────────────────────────────────────

function TabLirik({ k, muat }: { k: Kesiapan; muat: () => void }) {
  const [isi, setIsi] = useState(k.lirik_konten ?? '')
  const [pesan, setPesan] = useState('')
  const [sibuk, setSibuk] = useState(false)

  // Dihitung di layar SAAT MENGETIK, bukan hanya sesudah disimpan.
  //
  // Aturannya sama persis dengan server: baris tanpa [mm:ss.xx] tidak dihitung.
  // Kalau baru terlihat sesudah disimpan, orang akan menempelkan lirik polos,
  // menekan simpan, dan menyimpulkan liriknya sudah beres.
  const berwaktu = isi.split('\n').filter(b => /\[\d{1,2}:\d{1,2}([.:]\d{1,3})?\]/.test(b)).length
  const semua = isi.split('\n').filter(b => b.trim()).length

  async function simpan() {
    setSibuk(true); setPesan('')
    try {
      const r = await api.put<{ tersinkron: boolean; baris: number }>(
        `/api/lagu/${k.id}/lirik`, { konten: isi })
      setPesan(r.data.tersinkron
        ? `Tersimpan — ${r.data.baris} baris berwaktu, lirik akan berjalan di TV.`
        : 'Tersimpan, tapi TANPA timestamp — lirik tidak akan berjalan di layar TV.')
      muat()
    } catch (e) { setPesan(pesanGalat(e)) } finally { setSibuk(false) }
  }

  return (
    <div>
      <p className="text-[12px] text-text-3 leading-relaxed mb-2">
        Format .lrc: tiap baris diawali <span className="text-text-2">[mm:ss.xx]</span>.
        Baris tanpa waktu tetap disimpan tapi <span className="text-text-2">tidak</span> akan
        berjalan di TV — lirik yang menggulir asal lebih buruk daripada tanpa lirik.
      </p>
      <textarea value={isi} onChange={e => setIsi(e.target.value)} rows={14}
        spellCheck={false}
        placeholder={'[00:12.30] Baris pertama\n[00:16.80] Baris kedua'}
        className="w-full bg-bg-2 border border-border rounded-xl px-3 py-2.5
                   text-[12.5px] font-mono outline-none focus:border-gold/40" />

      <div className="mt-2 flex items-center gap-3 text-[11.5px]">
        <span className={berwaktu === semua && semua > 0 ? 'text-emerald-400' : 'text-amber-400'}>
          {berwaktu} dari {semua} baris punya waktu
        </span>
        {k.lirik_sumber && <span className="text-text-3">sumber saat ini: {k.lirik_sumber}</span>}
        <div className="flex-1" />
        <button onClick={simpan} disabled={sibuk}
          className="px-4 py-2 rounded-xl bg-gold text-black text-[12.5px] font-medium
                     disabled:opacity-50 flex items-center gap-1.5">
          <FileText size={13} /> Simpan lirik
        </button>
      </div>
      {pesan && <div className="mt-2 text-[12px] text-gold">{pesan}</div>}

      {/* 21 Okt 2026: tempel lirik resmi (teks polos) → PC menyelaraskan ke
          waktu nyanyian. Jalan keluar saat Whisper salah dengar / mengarang. */}
      <SelaraskanTeks songID={k.id} muat={muat} />
      {k.lirik_sumber === 'manual' && <GantiResmi songID={k.id} muat={muat} />}
    </div>
  )
}

// Lirik manual sengaja tidak ditimpa mesin; tombol ini izin eksplisit per lagu.
function GantiResmi({ songID, muat }: { songID: string; muat: () => void }) {
  const [pesan, setPesan] = useState('')
  const [sibuk, setSibuk] = useState(false)
  async function kirim() {
    if (!confirm('Lirik manual lagu ini akan DIGANTI lirik resmi LRCLIB (atau hasil mesin bila LRCLIB tidak punya). Lanjut?')) return
    setSibuk(true); setPesan('')
    try { const r = await api.post<{ pesan: string }>(`/api/lagu/${songID}/lirik-ganti-resmi`); setPesan(r.data.pesan); muat() }
    catch (e) { setPesan(pesanGalat(e)) } finally { setSibuk(false) }
  }
  return (
    <div className="mt-3 flex items-center gap-2 text-[12px]">
      <button onClick={kirim} disabled={sibuk}
        className="px-3 py-1.5 rounded-lg bg-bg-3 border border-border text-text-2 hover:border-gold-2 inline-flex items-center gap-1.5 disabled:opacity-50">
        <RotateCcw size={12} /> Ganti lirik manual ini dengan lirik resmi (LRCLIB)
      </button>
      {pesan && <span className="text-gold">{pesan}</span>}
    </div>
  )
}

function SelaraskanTeks({ songID, muat }: { songID: string; muat: () => void }) {
  const [buka, setBuka] = useState(false)
  const [teks, setTeks] = useState('')
  const [pesan, setPesan] = useState('')
  const [sibuk, setSibuk] = useState(false)
  async function kirim() {
    setSibuk(true); setPesan('')
    try {
      const r = await api.post<{ pesan: string }>(`/api/lagu/${songID}/lirik-selaraskan`, { teks })
      setPesan(r.data.pesan); muat()
    } catch (e) { setPesan(pesanGalat(e)) } finally { setSibuk(false) }
  }
  return (
    <div className="mt-4 border-t border-border pt-3">
      {!buka ? (
        <button type="button" onClick={() => setBuka(true)}
          className="text-[12px] text-gold underline underline-offset-2">
          Lirik hasil mesin salah? Tempel lirik resmi (teks biasa) → PC menyelaraskan ke waktu nyanyian
        </button>
      ) : (
        <div>
          <p className="text-[12px] text-text-3 leading-relaxed mb-2">
            Tempel lirik <b>teks biasa</b> (tanpa waktu), satu baris per baris nyanyian. Label seperti [Chorus] boleh ada,
            akan dilewati. PC mendengar lagunya untuk mengambil <i>waktu</i>, teksnya memakai yang kamu tempel.
          </p>
          <textarea value={teks} onChange={e => setTeks(e.target.value)} rows={10} spellCheck={false}
            placeholder={'Mengapa sepi tanpa kau di sini?\nTapi tiap bertemu naik darahku lagi\n…'}
            className="w-full bg-bg-2 border border-border rounded-xl px-3 py-2.5 text-[12.5px] outline-none focus:border-gold/40" />
          <div className="mt-2 flex items-center gap-2">
            <button onClick={kirim} disabled={sibuk || teks.trim().split(/\s+/).length < 10}
              className="px-4 py-2 rounded-xl bg-gold/15 border border-gold/40 text-gold text-[12.5px] disabled:opacity-40 inline-flex items-center gap-1.5">
              {sibuk ? <Loader2 size={13} className="animate-spin" /> : <Cpu size={13} />} Selaraskan di PC
            </button>
            {pesan && <span className="text-[12px] text-gold">{pesan}</span>}
          </div>
        </div>
      )}
    </div>
  )
}

// ── MELODI ACUAN ───────────────────────────────────────────────────────────

function TabMelodi({ k, muat }: { k: Kesiapan; muat: () => void }) {
  const [track, setTrack] = useState<TrackMidi[] | null>(null)
  const [pilih, setPilih] = useState<number | null>(null)
  const [offDetik, setOffDetik] = useState(0)
  const [offSemi, setOffSemi] = useState(0)
  const [pesan, setPesan] = useState('')
  const [galat, setGalat] = useState('')
  const [sibuk, setSibuk] = useState(false)

  async function ambilBerkas(f: File) {
    setGalat(''); setPesan(''); setTrack(null); setPilih(null)
    try {
      const t = bacaMidi(await f.arrayBuffer())
      setTrack(t)
      // Dipilihkan yang paling mungkin, TAPI tetap ditampilkan sebagai
      // pilihan yang bisa diganti — dan alasan tiap track ikut tertulis.
      const tebak = t.find(x => x.mungkinVokal)
      setPilih(tebak ? tebak.indeks : null)
    } catch (e) {
      setGalat(e instanceof Error ? e.message : 'Berkas MIDI tidak terbaca')
    }
  }

  async function simpan() {
    const t = track?.find(x => x.indeks === pilih)
    if (!t) { setGalat('Pilih dulu track melodinya'); return }
    setSibuk(true); setGalat(''); setPesan('')
    try {
      const r = await api.put<{ jml_nada: number }>(`/api/lagu/${k.id}/melodi`, {
        nada: t.nada, offset_semitone: offSemi, offset_detik: offDetik, sumber: 'midi',
      })
      setPesan(`Tersimpan — ${r.data.jml_nada} nada. Skor lagu ini sekarang menilai ketepatan nada.`)
      muat()
    } catch (e) { setGalat(pesanGalat(e)) } finally { setSibuk(false) }
  }

  async function hapus() {
    setSibuk(true)
    try {
      await api.put(`/api/lagu/${k.id}/melodi`, { nada: [] })
      setPesan('Melodi dihapus — skor kembali menilai ketepatan irama saja.')
      setTrack(null); setPilih(null); muat()
    } catch (e) { setGalat(pesanGalat(e)) } finally { setSibuk(false) }
  }

  return (
    <div>
      <p className="text-[12px] text-text-3 leading-relaxed">
        Melodi acuan menghidupkan mode skor <span className="text-text-2">Ketepatan Nada</span>.
        Tanpa ini, skor menilai ketepatan irama saja — dan layar skor mengatakannya
        apa adanya ke tamu.
      </p>

      {k.ada_melodi && (
        <div className="mt-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05]
                        px-3.5 py-3 flex items-center gap-3">
          <Check size={15} className="text-emerald-400 shrink-0" />
          <div className="text-[12.5px] flex-1">
            Sudah ada melodi acuan — {k.jml_nada} nada.
          </div>
          <button onClick={hapus} disabled={sibuk}
            className="text-[11.5px] text-red-400 border border-red-500/30 rounded-lg px-2.5 py-1">
            Hapus
          </button>
        </div>
      )}

      <label className="mt-3 block border border-dashed border-border rounded-xl
                        px-4 py-6 text-center cursor-pointer hover:border-gold/40">
        <Upload size={20} className="mx-auto text-text-3" />
        <div className="mt-2 text-[12.5px]">Pilih berkas MIDI (.mid)</div>
        <div className="text-[11px] text-text-3 mt-0.5">
          Dibaca di peramban — berkasnya tidak dikirim ke mana-mana.
        </div>
        <input type="file" accept=".mid,.midi,audio/midi" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) ambilBerkas(f) }} />
      </label>

      {galat && <div className="mt-3 text-[12.5px] text-red-400 leading-relaxed">{galat}</div>}

      {track && (
        <div className="mt-4">
          <div className="text-[12px] text-text-3 mb-2">
            Pilih track yang berisi melodi vokal. Menggabungkan semuanya akan
            menilai suara orang terhadap garis bass.
          </div>
          <div className="space-y-1.5">
            {track.map(t => (
              <button key={t.indeks} onClick={() => setPilih(t.indeks)}
                className={`w-full text-left rounded-xl border px-3.5 py-2.5 ${
                  pilih === t.indeks ? 'border-gold/50 bg-gold/[0.07]' : 'border-border bg-bg-2'}`}>
                <div className="flex items-center gap-2">
                  <Music4 size={13} className={t.mungkinVokal ? 'text-emerald-400' : 'text-text-3'} />
                  <span className="text-[12.5px] font-medium flex-1 truncate">{t.nama}</span>
                  <span className="text-[11px] text-text-3">{t.nada.length} nada</span>
                </div>
                <div className="text-[11px] text-text-3 mt-0.5">
                  {namaNot(t.terendah)}–{namaNot(t.tertinggi)}
                  {t.mungkinVokal
                    ? <span className="text-emerald-400/80"> · mungkin jalur vokal</span>
                    : <span className="text-amber-400/80"> · {t.alasan}</span>}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="text-[11.5px] text-text-3">
              Geser waktu (detik)
              {/* MIDI dan audio jarang mulai pada detik yang sama. Tanpa
                  penyesuaian ini setiap nada dinilai telat konstan, dan
                  skornya rendah merata tanpa ada yang tahu sebabnya. */}
              <input type="number" step="0.1" value={offDetik}
                onChange={e => setOffDetik(Number(e.target.value) || 0)}
                className="mt-1 w-full bg-bg-2 border border-border rounded-lg
                           px-2.5 py-1.5 text-[12.5px] text-text outline-none" />
            </label>
            <label className="text-[11.5px] text-text-3">
              Geser nada (semitone)
              <input type="number" step="1" value={offSemi}
                onChange={e => setOffSemi(Number(e.target.value) || 0)}
                className="mt-1 w-full bg-bg-2 border border-border rounded-lg
                           px-2.5 py-1.5 text-[12.5px] text-text outline-none" />
            </label>
          </div>

          <button onClick={simpan} disabled={sibuk || pilih === null}
            className="mt-4 w-full py-2.5 rounded-xl bg-gold text-black text-[13px]
                       font-medium disabled:opacity-40">
            Simpan sebagai melodi acuan
          </button>
        </div>
      )}

      {pesan && <div className="mt-3 text-[12.5px] text-gold">{pesan}</div>}
    </div>
  )
}
